import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Inbox, Users, Map, FileEdit, GraduationCap, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CATEGORY_ICON, ROLE_LABEL, formatDate } from "@/lib/constants";
import { TripleSlash } from "@/components/brand/Logo";
import { EmptyState } from "@/components/Badges";
import { StatTile, TrainingItemRow } from "@/components/training/TrainingBits";
import { RegionAdminWelcome } from "@/components/RegionAdminWelcome";

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

const Stat = ({ label, value, to, icon: Icon, testId }) => (
  <Link to={to} data-testid={testId} className="group rounded-2xl border border-lasso-line bg-white p-5 transition-colors duration-200 hover:border-lasso-ink">
    <div className="flex items-center justify-between text-zinc-500">
      <Icon className="h-4 w-4" />
      <ArrowUpRight className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
    </div>
    <p className="mt-6 font-display text-3xl font-bold" data-testid={`${testId}-value`}>{value}</p>
    <p className="mt-1 text-xs uppercase tracking-[0.16em] text-zinc-500">{label}</p>
  </Link>
);

export const SopRow = ({ sop, testId }) => {
  const Icon = CATEGORY_ICON[sop.category] || BookOpen;
  return (
    <Link to={`/sops/${sop.id}`} data-testid={testId} className="group flex items-start gap-4 rounded-xl px-3 py-4 transition-colors duration-200 hover:bg-lasso-tint">
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-lasso-paper text-lasso-deep group-hover:bg-white"><Icon className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{sop.title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{sop.category} · v{sop.version} · {sop.step_count} steps · Updated {formatDate(sop.updated_at)}</p>
      </div>
      <ArrowUpRight className="h-4 w-4 shrink-0 text-zinc-400 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-lasso-ink" />
    </Link>
  );
};

const Panel = ({ title, action, children, testId }) => (
  <section className="rounded-2xl border border-lasso-line bg-white p-6" data-testid={testId}>
    <div className="mb-3 flex items-center justify-between px-3">
      <h2 className="font-display text-sm font-bold uppercase tracking-[0.14em]">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const [training, setTraining] = useState(null);
  const [team, setTeam] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
    api.get("/me/training").then((r) => setTraining(r.data));
    if (["admin", "regional_manager", "community_manager"].includes(user.role)) api.get("/reports/overview").then((r) => setTeam(r.data));
  }, [user.role]);

  const upNext = (training?.items || []).filter((i) => i.status !== "completed").slice(0, 4);

  const place = user.community_name || user.region_name || "Lasso Communities";

  return (
    <div className="fade-up">
      {user.is_region_admin && <RegionAdminWelcome />}
      <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <TripleSlash />
          <h1 className="mt-5 font-display text-3xl font-bold tracking-tight sm:text-4xl" data-testid="dashboard-welcome">
            {greeting()}, {user.name.split(" ")[0]}.
          </h1>
          <p className="mt-3 text-muted-foreground" data-testid="dashboard-role-context">{ROLE_LABEL[user.role]} · {place}</p>
        </div>
        <Link to="/sops" className="inline-flex items-center gap-2 self-start rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-lasso-navy md:self-auto" data-testid="dashboard-browse-sops">
          Browse SOP library <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>

      {data?.admin && (
        <div className="mb-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat label="Pending requests" value={data.admin.pending_requests} to="/admin/requests" icon={Inbox} testId="dashboard-stat-pending" />
          <Stat label="Active users" value={data.admin.active_users} to="/admin/users" icon={Users} testId="dashboard-stat-users" />
          <Stat label="Communities" value={data.admin.communities} to="/admin/org" icon={Map} testId="dashboard-stat-communities" />
          <Stat label="Draft SOPs" value={data.admin.draft_sops} to="/sops" icon={FileEdit} testId="dashboard-stat-drafts" />
        </div>
      )}
      {data?.region && (
        <div className="mb-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat label="Active team members" value={data.region.team_size} to="/team" icon={Users} testId="dashboard-stat-team" />
          <Stat label="Communities in region" value={data.region.communities} to="/team" icon={Map} testId="dashboard-stat-region-communities" />
        </div>
      )}

      {training && (
        <>
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="dashboard-training-stats">
            <StatTile label="Assigned to me" value={training.summary.assigned} testId="dashboard-stat-assigned" />
            <StatTile label="Completed" value={training.summary.completed} testId="dashboard-stat-completed" />
            <StatTile label="Overdue" value={training.summary.overdue} tone="danger" testId="dashboard-stat-overdue" />
            <StatTile label="Certificates" value={training.summary.certificates} testId="dashboard-stat-certificates" />
          </div>
          {upNext.length > 0 && (
            <section className="mb-10" data-testid="dashboard-up-next">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-display text-sm font-bold uppercase tracking-[0.14em]">Up next</h2>
                <Link to="/training" className="text-xs font-semibold hover:underline" data-testid="dashboard-view-training">View all training</Link>
              </div>
              <div className="grid gap-3 md:grid-cols-2">{upNext.map((i) => <TrainingItemRow key={`${i.item_type}-${i.item_id}`} item={i} />)}</div>
            </section>
          )}
        </>
      )}

      <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <Panel title={`SOPs for ${ROLE_LABEL[user.role]}s`} testId="dashboard-my-sops" action={<span className="text-xs text-muted-foreground">{data?.my_sop_count ?? "–"} published</span>}>
          {data && (data.my_sops.length ? data.my_sops.map((s) => <SopRow key={s.id} sop={s} testId={`dashboard-my-sop-${s.id}`} />) : <EmptyState title="No SOPs tagged for your role yet" />)}
        </Panel>
        <div className="space-y-6">
          <Panel title="Recently updated" testId="dashboard-recent-sops">
            {data && data.recent_sops.slice(0, 4).map((s) => <SopRow key={s.id} sop={s} testId={`dashboard-recent-sop-${s.id}`} />)}
          </Panel>
          {team && (
            <Link to="/reports" className="block rounded-2xl bg-lasso-ink p-6 text-white transition-colors hover:bg-lasso-navy" data-testid="dashboard-team-progress">
              <GraduationCap className="h-5 w-5 text-lasso-sky" />
              <p className="mt-4 text-xs uppercase tracking-[0.16em] text-white/60">Team training · {team.scope_label}</p>
              <p className="mt-2 font-display text-3xl font-bold" data-testid="dashboard-team-completion">{team.totals.pct}% complete</p>
              <p className="mt-1 text-sm text-white/70">{team.totals.completed}/{team.totals.assigned} items · {team.totals.overdue} overdue</p>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
